import Service from '@ember/service';
import { service } from '@ember/service';
import type FinancialInstitutionModel from 'tio-common/models/financial-institution';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type RemoteEventModel from 'tio-common/models/remote-event';
import type {
  RemoteEventLogLevel,
  RemoteEventProviderContext,
} from 'tio-common/models/remote-event';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

import type SessionContextService from './session-context';

export type LogLevel = RemoteEventLogLevel;
export type ProviderComponent = 'LoginSpinwheel' | 'LoginFinicityConnectLite';

type ObservabilityEventType =
  | 'INITIALIZE'
  | 'UPLOAD_START'
  | 'UPLOAD_FAILURE'
  | 'UPLOAD_SUCCESS'
  | 'PROCESSING_SUCCESS'
  | 'PROCESSING_PARTIAL_SUCCESS'
  | 'PROCESSING_FAILURE'
  | 'DISPUTE_SUBMITTED';
interface ObservabilityEventOptions {
  eventName: ObservabilityEventType;
  component: string;
  document?: ObservableDocumentModel;
}

export default class RemoteEventLoggerService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  async log(
    provider: 'spinwheel' | 'finicity',
    context: RemoteEventProviderContext,
    logLevel: LogLevel = 'INFO',
    component: ProviderComponent,
    institution?: FinancialInstitutionModel,
    callback?: string
  ) {
    const remoteEvent = this.store.createRecord<RemoteEventModel>('remote-event', {
      eventType: 'REMOTE_LOGGER',
      payload: {
        logLevel: logLevel,
        data: {
          event: {
            id: crypto.randomUUID(),
            institution: {
              id: institution?.id || 'unknown',
              name: institution?.name || 'unknown',
            },
            company: {
              id: this.sessionContext.currentRole?.company?.id || 'unknown',
              name: this.sessionContext.currentRole?.company.name || 'unknown',
            },
            [provider]: {
              context,
              callback,
            },
          },
          component,
        },
      },
    });
    remoteEvent.user = this.sessionContext.user;
    return remoteEvent.save();
  }

  async logObservabilityEvent(options: ObservabilityEventOptions) {
    const { document } = options;
    const structuredContent = document?.structuredContent;
    const servicerName =
      structuredContent && 'servicer_name' in structuredContent
        ? (structuredContent.servicer_name ?? 'unknown')
        : 'unknown';
    const pseudoAccounts =
      structuredContent && 'pseudo_accounts' in structuredContent
        ? (structuredContent.pseudo_accounts?.join(',') ?? 'unknown')
        : 'unknown';

    const remoteEvent = this.store.createRecord<RemoteEventModel>('remote-event', {
      eventType: 'REMOTE_LOGGER',
      payload: {
        logLevel: 'INFO',
        data: {
          component: options.component,
          event: {
            eventName: options.eventName,
            id: crypto.randomUUID(),
            company: {
              id: this.sessionContext.currentRole?.company?.id || 'unknown',
              name: this.sessionContext.currentRole?.company.name || 'unknown',
            },
            document: { id: document?.id || 'unknown' },
            asset: { id: document?.observableAsset?.id || 'unknown' },
            servicer_name: servicerName,
            pseudo_accounts: pseudoAccounts,
            documentType: document?.provider || 'unknown',
          },
        },
      },
    });
    remoteEvent.user = this.sessionContext.user;
    return remoteEvent.save();
  }
}
